/**
 * Shared QuestionBlock Component
 *
 * Renders the common question-answer grid pattern used by both
 * CompanyAnswers (V2 layout) and IndividualAnswers. The layout is:
 *
 *   Row 1: Question index + question text  |  Main answer
 *   Row 2 (optional): Description text     |  Optional side content
 *   Custom content rows (injected via `extraContent` prop)
 *   Row N+: Subquestion text               |  Subquestion answer
 *
 * Each row is a CSS grid row so heights align between the question
 * and answer columns. Company-specific extras (e.g. political danger
 * rows, expandable registry info) are injected through the
 * `extraContent` render prop without polluting this shared component.
 */
import React from "react";
import cx from "classnames";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleExclamation } from "@fortawesome/pro-solid-svg-icons";
import { AnswerDto } from "../../../../types/CompanyAnswersTypes";
import { buildAnswerText, hasAnyWrongAnswer, hasAnswer } from "./answerHelpers";

interface QuestionBlockProps {
    /** The question data to render */
    question: AnswerDto;
    /** Array index used as React key */
    index: number;
    /** Pre-computed display text for the main answer */
    answerDisplayText: string;
    /**
     * Whether to render the description as a row below the question.
     * Set to false when the description is displayed as an inline tooltip instead.
     * Defaults to true.
     */
    showDescriptionRow?: boolean;
    /** Optional tooltip rendered inline next to the question title text */
    titleTooltip?: React.ReactNode;
    /** Optional content placed in the answer cell of the description row
     *  (e.g. PoliticalPersonList for political exposure questions) */
    descriptionSideContent?: React.ReactNode;
    /** Optional custom rows rendered between the description and subquestions
     *  (e.g. Q8 political danger, expandable registry info, high-risk countries) */
    extraContent?: React.ReactNode;
}

const QuestionBlock: React.FC<QuestionBlockProps> = ({
    question,
    index,
    answerDisplayText,
    showDescriptionRow = true,
    titleTooltip,
    descriptionSideContent,
    extraContent,
}) => {
    /** Whether any answer in this block is flagged — drives indent alignment */
    const needsIndent = hasAnyWrongAnswer(question);

    return (
        <div className={cx("question-block")} key={index}>
            {/* Question number sits outside the wrapper for consistent alignment */}
            <span className="question-index paragraph-small--bold">
                {question.mainQuestionOrder}.
            </span>

            <div className="question-wrapper">
                {/* ROW 1: Main question text and primary answer */}
                <div className="question-row">
                    <div className="question-row__question">
                        <p className="paragraph-small--bold question-title">
                            {question.question}
                            {titleTooltip}
                        </p>
                    </div>

                    <div className={cx("question-row__answer", {
                        "question-row__answer--indent": needsIndent,
                        "questionIsNotWrong": !question.isWrong
                    })}>
                        {question.isWrong && (
                            <FontAwesomeIcon className="fa-icon fa-icon--warning" icon={faCircleExclamation} />
                        )}
                        <p className="paragraph-small--bold answer-text">
                            {answerDisplayText}
                        </p>
                    </div>
                </div>

                {/* ROW 2 (optional): Description shown below the question */}
                {question.questionDescription && showDescriptionRow && (
                    <div className="question-row">
                        <div className="question-row__question">
                            <p className="paragraph-small--italic question-description">
                                {question.questionDescription}
                            </p>
                        </div>
                        <div className={cx("question-row__answer", {
                            "question-row__answer--indent": needsIndent,
                            "questionIsNotWrong": !question.isWrong
                        })}>
                            {descriptionSideContent}
                        </div>
                    </div>
                )}

                {/* Custom content rows injected by the parent (company-specific extras) */}
                {extraContent}

                {/* Subquestion rows — each gets its own grid row for height alignment */}
                {question.subQuestions && question.subQuestions.length > 0 && (
                    question.subQuestions.filter(hasAnswer).map((subQ, subIndex) => (
                        <div className="question-row" key={subIndex}>
                            <div className="question-row__question">
                                <p className="paragraph-small subquestion-text">
                                    {subQ.question}
                                </p>
                            </div>

                            <div className={cx("question-row__answer", {
                                "question-row__answer--indent": needsIndent,
                                "questionIsNotWrong": !subQ.isWrong
                            })}>
                                {subQ.isWrong && (
                                    <FontAwesomeIcon className="fa-icon fa-icon--warning" icon={faCircleExclamation} />
                                )}
                                <span className="paragraph-small--bold">
                                    {subQ.optionTextAnswer
                                        ? buildAnswerText(subQ.answerBoolean, subQ.optionTextAnswer)
                                        : buildAnswerText(subQ.answerBoolean, subQ.answertext)}
                                </span>
                            </div>
                        </div>
                    ))
                )}
            </div>
        </div>
    );
};

export default QuestionBlock;
